import React, { useState } from 'react';
import {
    TextField,
    Button,
    Box,
    Typography,
    Grid,
    CircularProgress, Breadcrumbs,
} from '@mui/material';
import api from '../../api';
import {Link, useNavigate} from 'react-router-dom';
import { NumericFormat } from 'react-number-format';
import DidNotLoad from "../../components/DidNotLoad";
import HtmlColorField from '../../components/HtmlColorField';
import { parseFormattedMoneyToCents } from '../../helpers/formatters';
import { isHtmlHexColor, normalizeHtmlHex } from '../../helpers/htmlColor';

function RegisterMaterialPage() {
    const [materialType, setMaterialType] = useState('');
    const [brand, setBrand] = useState('');
    const [color, setColor] = useState('#000000');
    const [colorLabel, setColorLabel] = useState('');
    const [costPerKilogram, setCostPerKilogram] = useState('');
    const [costPerKilogramFloat, setCostPerKilogramFloat] = useState(undefined);
    const [spoolWeight, setSpoolWeight] = useState('1000'); // gramas
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const [resultErrors, setResultErrors] = useState({
        brand: null,
        type: null,
        color: null,
        color_label: null,
        cost_per_kilogram: null,
        spool_weight: null,
        has_errors: false
    });
    const [hasResponse, setHasResponse] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);

    const handleSubmit = (event) => {
        event.preventDefault();

        let hasError = false
        if (materialType === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors, // Mantém os valores existentes
                type: 'O tipo do material é obrigatório.', // Atualiza o campo 'brand'
                has_errors: true, // Atualiza o indicador de erro
            }));
        }

        if (brand === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors, // Mantém os valores existentes
                brand: 'A marca do material é obrigatório.', // Atualiza o campo 'brand'
                has_errors: true, // Atualiza o indicador de erro
            }));
        }

        if (color === '' || !isHtmlHexColor(color)) {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors,
                color: 'Selecione a cor no seletor (notação HTML #RRGGBB).',
                has_errors: true,
            }));
        }

        if (colorLabel.trim() === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors,
                color_label: 'O nome da cor é obrigatório.',
                has_errors: true,
            }));
        }

        if (spoolWeight === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors,
                spool_weight: 'O peso do carretel é obrigatório.',
                has_errors: true,
            }));
        }

        if (hasError) {
            return;
        }

        if (isSubmitting) return; // Evita duplo clique
        setIsSubmitting(true); // Ativa o estado de loading

        const costPerKgInCents = parseFormattedMoneyToCents(costPerKilogramFloat, costPerKilogram);
        const spoolWeightGrams = parseInt(spoolWeight, 10);
        const newMaterial = {
            type: materialType,
            brand,
            color: normalizeHtmlHex(color),
            color_label: colorLabel.trim(),
            spool_weight: spoolWeightGrams,
            current_weight: spoolWeightGrams,
            cost: costPerKgInCents,
        };

        api.post('filaments', newMaterial)
            .then((response) => {
                setHasResponse(true);
                console.log('Filamento criado com sucesso:', response);
                navigate('/admin/materials'); // Redireciona de volta para a lista
            })
            .catch((error) => {
                try {
                    const data = error.response.data;
                    setResultErrors({
                        ...data.errors,
                        has_errors: true,
                    });
                    setHasResponse(true);
                } catch (error) {
                    console.log(error);
                }
            })
            .finally(() => {
                setIsSubmitting(false);
                if (!hasResponse) {
                    setDidNotLoad(true);
                }
            });
    };

    if (didNotLoad) {
        return <DidNotLoad/>
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/materials')} to={'/admin/materials'}>
                    Filamentos
                </Link>
                <Typography color="text.primary">Adicionando novo filamento</Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>Adicionar Novo Filamento</Typography>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Tipo de Material (Filamento PLA, Resina, etc)"
                            fullWidth
                            value={materialType}
                            onChange={(e) => setMaterialType(e.target.value)}
                        />
                        {resultErrors.type && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.type}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Marca"
                            fullWidth
                            value={brand}
                            onChange={(e) => setBrand(e.target.value)}
                        />
                        {resultErrors.brand && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.brand}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <HtmlColorField
                            label="Cor (hex)"
                            value={color}
                            onChange={setColor}
                            error={Boolean(resultErrors.color)}
                        />
                        {resultErrors.color && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.color}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Nome da cor"
                            fullWidth
                            value={colorLabel}
                            onChange={(e) => setColorLabel(e.target.value)}
                            placeholder="Ex.: Verde menta, Preto fosco"
                            error={Boolean(resultErrors.color_label)}
                        />
                        {resultErrors.color_label && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.color_label}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <NumericFormat
                            label="Custo por Kilo"
                            fullWidth
                            customInput={TextField}
                            value={costPerKilogram}
                            onValueChange={(values) => {
                                setCostPerKilogram(values.value);
                                setCostPerKilogramFloat(values.floatValue);
                            }}
                            thousandSeparator="."
                            decimalSeparator=","
                            prefix="R$ "
                            decimalScale={2}
                            fixedDecimalScale
                            inputMode="numeric"
                        />
                        {resultErrors.cost_per_kilogram && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.cost_per_kilogram}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Peso do carretel (1000g, 500g, 250g)"
                            fullWidth
                            type="number"
                            value={spoolWeight}
                            onChange={(e) => setSpoolWeight(e.target.value)}
                            inputMode="numeric"
                        />
                        {resultErrors.spool_weight && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.spool_weight}
                            </div>
                        )}
                    </Grid>
                </Grid>
                <Box sx={{display: 'flex', justifyContent: 'flex-end', marginTop: 2}}>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting} // Desativa o botão durante o envio
                        startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                        fullWidth
                    >
                        {isSubmitting ? 'Cadastrando...' : 'Cadastrar Filamento'}
                    </Button>
                </Box>
            </form>
        </div>
    );
}

export default RegisterMaterialPage;
